import { useQuery } from '@tanstack/react-query'
import dashboardStatusOptions from '../components/Dashboard/DashboardStatusOptions.json'
import { getCiamSessionToken } from './useCiamAuthorize'

const DEFAULT_VERIFICATION_STATUS = 'Status unknown'

const statusOptionsByVerificationStatus = new Map(
  dashboardStatusOptions.map(option => [
    String(option.VerificationStatus),
    option,
  ])
)

const getAccountStatusDetails = verificationStatus => {
  const status = String(verificationStatus ?? '').trim()

  return (
    statusOptionsByVerificationStatus.get(status) ??
    statusOptionsByVerificationStatus.get(DEFAULT_VERIFICATION_STATUS)
  )
}

const withAccountStatusDetails = account => ({
  ...account,
  accountStatusDetails: getAccountStatusDetails(account.VerificationStatus),
})

const errorMsgs = [
  `Nothing found for this Reference ID.`,
  `Something went wrong. Please contact support or try again later.`,
  `Our systems are down. Please try again at a later time.`,
]

export const UNMET_REQUIREMENTS_STATUS = 'Unmet requirements'

export const CANCELLED_INSURANCE_STATUSES = [
  'Expired insurance',
  'No insurance provided',
  'Canceled insurance',
  'CPI purchased',
  'LPI purchased',
]

export const PENDING_STATUSES = ['Status unknown', 'Awaiting review']

const getVerificationStatus = account =>
  String(account?.VerificationStatus ?? '').trim()

const chipVariantFromIconColor = iconColor => {
  switch (iconColor) {
    case 'warning':
      return 'warning'
    case 'info':
      return 'info'
    case 'error':
      return 'error'
    case 'success':
      return 'success'
    default:
      return 'gray'
  }
}

export const getSingleAccountChipCount = account => {
  const requirements = account.CoverageRequirements ?? []
  const verificationStatus = getVerificationStatus(account)

  if (CANCELLED_INSURANCE_STATUSES.includes(verificationStatus)) {
    return requirements.length
  }

  if (PENDING_STATUSES.includes(verificationStatus)) {
    return 0
  }

  return requirements.filter(req => req.Status === false).length
}

export const getSingleAccountChipVariant = account => {
  const verificationStatus = getVerificationStatus(account)

  if (CANCELLED_INSURANCE_STATUSES.includes(verificationStatus)) {
    return 'error'
  }

  return chipVariantFromIconColor(account.accountStatusDetails?.iconColor)
}

const toAccountList = source => {
  if (Array.isArray(source)) return source
  if (source) return [source]
  return []
}

export const getRequirementChipCount = source =>
  toAccountList(source).reduce(
    (total, account) => total + getSingleAccountChipCount(account),
    0
  )

export const getRequirementChipVariant = source => {
  const list = toAccountList(source)
  if (list.length === 0) return 'gray'
  if (list.length === 1) return getSingleAccountChipVariant(list[0])

  if (
    list.some(account =>
      CANCELLED_INSURANCE_STATUSES.includes(getVerificationStatus(account))
    )
  ) {
    return 'error'
  }

  if (
    list.some(
      account => getVerificationStatus(account) === UNMET_REQUIREMENTS_STATUS
    )
  ) {
    return 'warning'
  }

  return 'gray'
}

const fetchCoverageStatus = async (refId, agencyId) => {
  const sessionToken = getCiamSessionToken() ?? ''

  const response = await fetch(
    `${process.env.GATSBY_API_URL}/unitrac/property/coverage-status?ReferenceId=${refId}&AgencyId=${agencyId}&Username=username`,
    {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${sessionToken}`,
      },
    }
  )

  if (!response.ok) {
    if (response.status === 404) {
      throw new Error(errorMsgs[0])
    }
    if (response.status === 503) {
      throw new Error(errorMsgs[2])
    }
    if (response.status >= 500) {
      throw new Error(errorMsgs[1])
    }
    if (response.status >= 300) {
      throw new Error(errorMsgs[0])
    }
  }

  return response.json()
}

export const collectUniqueLoanIds = coverageResults => {
  const loanIds = new Set()

  for (const coverageData of coverageResults) {
    const accounts = toAccountList(coverageData)

    for (const account of accounts) {
      if (account?.LoanID != null) {
        loanIds.add(account.LoanID)
      }
    }
  }

  return [...loanIds]
}

export const fetchMultiCollatteral = async loanIds => {
  const sessionToken = getCiamSessionToken() ?? ''

  const response = await fetch(
    `${process.env.GATSBY_API_URL}/reference-Ids/multicollateral`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${sessionToken}`,
      },
      body: JSON.stringify({
        loanIds,
      }),
    }
  )

  if (!response.ok) {
    if (response.status === 404) {
      throw new Error(errorMsgs[0])
    }
    if (response.status === 503) {
      throw new Error(errorMsgs[2])
    }
    if (response.status >= 500) {
      throw new Error(errorMsgs[1])
    }
  }

  return response.json()
}

const resolveAccountsWithAdditionalCoverages = async (data, agencyId) => {
  if (!data?.ReferenceID) return []

  const primaryAccount = data
  const additionalRefIds = [
    ...new Set(
      (primaryAccount.AdditionalCoveragesArray ?? [])
        .map(item => item?.ReferenceID)
        .filter(id => id && id !== primaryAccount.ReferenceID)
    ),
  ]

  if (additionalRefIds.length === 0) {
    return [primaryAccount]
  }

  const additionalAccounts = await Promise.all(
    additionalRefIds.map(async additionalRefId => {
      try {
        const additionalData = await fetchCoverageStatus(
          additionalRefId,
          agencyId
        )
        return additionalData?.ReferenceID ? additionalData : null
      } catch {
        return null
      }
    })
  )

  return [
    primaryAccount,
    ...additionalAccounts.filter(item => item?.ReferenceID),
  ]
}

export const fetchCoverageRequirements = async (refId, agencyId) => {
  const data = await fetchCoverageStatus(refId, agencyId)
  const accounts = await resolveAccountsWithAdditionalCoverages(data, agencyId)

  if (accounts.length === 0) {
    throw new Error(errorMsgs[0])
  }

  return accounts.map(withAccountStatusDetails)
}

export const useGetCoverageRequirements = (refId, agencyId, options = {}) => {
  const { enabled = !!refId } = options

  return useQuery({
    queryKey: ['coverageRequirements', refId, agencyId],
    queryFn: () => fetchCoverageRequirements(refId, agencyId),
    enabled: !!(refId && agencyId) && enabled,
    staleTime: 5 * 60 * 1000,
    cacheTime: 10 * 60 * 1000,
  })
}
